/** @format */

/** Browser speechSynthesis limit — long table extracts are truncated. */
const MAX_SPEAK_CHARS = 8000;

/** Max table rows read aloud (keeps speech under char limit). */
const MAX_TABLE_ROWS_FOR_SPEECH = 25;

/**
 * @param {object[]} rows
 * @returns {string[]}
 */
function getVisibleTableColumns(rows) {
  const first = rows[0];
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first).filter((col) => {
    if (col === 'id' || col === 'related_object' || col === 'related_record_id') return false;
    const lower = col.toLowerCase();
    if (lower === 'ownerid' || lower === 'owner_id') return false;
    return true;
  });
}

/**
 * @param {object[]} rows
 * @param {Record<string, string>} [columnLabels]
 * @param {(col: string, columnLabels?: Record<string, string>) => string} getColumnLabel
 * @param {(col: string, value: unknown) => string} formatCellValue
 * @returns {string}
 */
function formatTableForSpeech(rows, columnLabels, getColumnLabel, formatCellValue) {
  if (!Array.isArray(rows) || rows.length === 0) return '';

  const columns = getVisibleTableColumns(rows);
  if (columns.length === 0) return '';

  const lines = [`Table with ${rows.length} row${rows.length !== 1 ? 's' : ''}.`];
  const limited = rows.slice(0, MAX_TABLE_ROWS_FOR_SPEECH);

  limited.forEach((row, idx) => {
    const cells = columns
      .map((col) => {
        const label = getColumnLabel(col, columnLabels);
        const display = formatCellValue(col, row[col]);
        if (display == null || display === '') return null;
        return `${label}: ${display}`;
      })
      .filter(Boolean);

    if (cells.length > 0) {
      lines.push(`Row ${idx + 1}. ${cells.join('. ')}.`);
    }
  });

  if (rows.length > MAX_TABLE_ROWS_FOR_SPEECH) {
    lines.push(
      `And ${rows.length - MAX_TABLE_ROWS_FOR_SPEECH} more row${rows.length - MAX_TABLE_ROWS_FOR_SPEECH !== 1 ? 's' : ''} not read aloud.`
    );
  }

  return lines.join(' ');
}

/**
 * @param {object | null | undefined} msg - Assistant message from AiChatbot state
 * @param {{ getColumnLabel?: (col: string, columnLabels?: Record<string, string>) => string, formatCellValue?: (col: string, value: unknown) => string }} [options]
 * @returns {string} Text suitable for read-aloud (text + table; excludes charts)
 */
export function getAssistantSpeakableText(msg, options = {}) {
  if (!msg) return '';

  const { getColumnLabel, formatCellValue } = options;
  const parts = [];

  if (msg.documentParse) {
    if (typeof msg.transformedOutput === 'string' && msg.transformedOutput.trim()) {
      parts.push(msg.transformedOutput.trim());
    } else if (typeof msg.parsedText === 'string' && msg.parsedText.trim()) {
      parts.push(msg.parsedText.trim());
    }
    return parts.join('\n\n').trim().slice(0, MAX_SPEAK_CHARS);
  }

  if (typeof msg.text === 'string' && msg.text.trim()) {
    parts.push(msg.text.trim());
  } else if (typeof msg.error === 'string' && msg.error.trim()) {
    parts.push(msg.error.trim());
  }

  if (
    Array.isArray(msg.data) &&
    msg.data.length > 0 &&
    typeof getColumnLabel === 'function' &&
    typeof formatCellValue === 'function'
  ) {
    const tableSpeech = formatTableForSpeech(
      msg.data,
      msg.columnLabels,
      getColumnLabel,
      formatCellValue
    );
    if (tableSpeech) parts.push(tableSpeech);
  }

  return parts.join('\n\n').trim().slice(0, MAX_SPEAK_CHARS);
}

export function isTextToSpeechSupported() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export function stopTextToSpeech() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
}

/**
 * @param {string} text
 * @param {{ onStart?: () => void, onEnd?: () => void, onError?: (err: SpeechSynthesisErrorEvent) => void }} [callbacks]
 * @returns {boolean}
 */
export function speakText(text, callbacks = {}) {
  if (!isTextToSpeechSupported()) return false;

  const trimmed = String(text || '').trim();
  if (!trimmed) return false;

  stopTextToSpeech();

  const utterance = new SpeechSynthesisUtterance(trimmed.slice(0, MAX_SPEAK_CHARS));
  utterance.rate = 1;
  utterance.pitch = 1;
  utterance.lang =
    (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

  utterance.onstart = () => {
    callbacks.onStart?.();
  };
  utterance.onend = () => {
    callbacks.onEnd?.();
  };
  utterance.onerror = (event) => {
    if (event.error !== 'canceled') {
      callbacks.onError?.(event);
    }
    callbacks.onEnd?.();
  };

  window.speechSynthesis.speak(utterance);
  return true;
}
